import React, { useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import {
  ArrowUpRight, ArrowDown, Menu, X, Sparkles, Palette, Heart, Clock,
  Baby, MapPin, ChevronDown, Mail, Facebook, Instagram, Globe,
} from "lucide-react";
import useLenis from "@/hooks/useLenis";
import { Reveal, LineReveal, WordsReveal } from "@/components/site/Reveal";
import QuoteForm from "@/components/site/QuoteForm";
import BrushDivider, { BrushIcon, Aureole } from "@/components/site/BrushDivider";
import { photos, LOGO_URL } from "@/data/photos";

/* ============================================================
   Shared: Nav
   ============================================================ */
const NAV = [
  { href: "#accueil", label: "Accueil", slug: "accueil" },
  { href: "#prestations", label: "Prestations", slug: "prestations" },
  { href: "#tarifs", label: "Tarifs", slug: "tarifs" },
  { href: "#histoire", label: "À propos", slug: "a-propos" },
  { href: "#galerie", label: "Galerie", slug: "galerie" },
  { href: "#faq", label: "FAQ", slug: "faq" },
  { href: "#contact", label: "Contact", slug: "contact" },
];

const LogoMark = ({ className = "", src = LOGO_URL, wrapperClass = "" }) => (
  <img
    src={src}
    alt="Les Couleurs de Manou — Maquillage artistique"
    className={`${wrapperClass} ${className}`}
    style={{ width: "auto", objectFit: "contain" }}
    loading="eager"
  />
);

const Nav = () => {
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  const bg = useTransform(scrollY, [0, 80], ["rgba(251,247,241,0)", "rgba(251,247,241,0.92)"]);
  const border = useTransform(scrollY, [0, 80], ["rgba(0,0,0,0)", "rgba(61,46,54,0.08)"]);
  return (
    <motion.header
      data-testid="site-nav"
      style={{ backgroundColor: bg, borderBottomColor: border }}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-md border-b transition-colors"
    >
      <div className="max-w-[1440px] mx-auto flex items-center justify-between px-6 md:px-10 h-16 md:h-20">
        <a href="#accueil" data-testid="nav-brand" className="font-serif italic text-lg md:text-xl" style={{ color: "var(--ink)" }}>
          Les Couleurs de Manou
        </a>
        <nav className="hidden lg:flex items-center gap-9 text-sm font-medium" style={{ color: "var(--ink-soft)" }}>
          {NAV.map((l) => (
            <a key={l.href} href={l.href} data-testid={`nav-${l.slug}`} className="nav-link">
              {l.label}
            </a>
          ))}
        </nav>
        <a
          href="#contact"
          data-testid="nav-cta"
          className="hidden md:inline-flex items-center gap-2 rounded-full text-sm font-semibold px-5 py-2.5 transition-all"
          style={{ background: "#D48699", color: "#fff" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#BC6B80")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#D48699")}
        >
          Demander un devis
          <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
        <button
          data-testid="nav-menu-toggle"
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/60 ml-auto"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="nav-mobile"
          className="lg:hidden px-6 py-8 border-t"
          style={{ backgroundColor: "var(--bg)", borderColor: "var(--line)" }}
        >
          <ul className="space-y-4 text-2xl font-serif">
            {NAV.map((l) => (
              <li key={l.href}>
                <a href={l.href} onClick={() => setOpen(false)} className="block py-1">{l.label}</a>
              </li>
            ))}
          </ul>
          <a href="#contact" onClick={() => setOpen(false)} className="btn-primary mt-6">
            Demander un devis <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </motion.div>
      )}
    </motion.header>
  );
};

/* ============================================================
   HERO — logo + copy + circular photo
   ============================================================ */
const Hero = () => {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const yPhoto = useTransform(scrollY, [0, 800], [0, 120]);
  const scalePhoto = useTransform(scrollY, [0, 800], [1, 1.06]);

  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-24 md:pt-28 pb-20 md:pb-24 overflow-hidden"
      style={{ backgroundColor: "var(--bg)" }}
    >
      {/* Watercolor blobs */}
      <div className="blob blob-blush w-[560px] h-[560px] -top-40 -left-40 float-y" />
      <div className="blob blob-lavender w-[480px] h-[480px] top-24 -right-24 float-y" style={{ animationDelay: '2s' }} />
      <div className="blob blob-sage w-[420px] h-[420px] bottom-0 left-1/3 float-y" style={{ animationDelay: '4s' }} />
      <div className="blob blob-peach w-[380px] h-[380px] bottom-10 -right-16 float-y" style={{ animationDelay: '1s' }} />
      <div className="blob blob-turquoise w-[320px] h-[320px] top-1/2 right-1/4 float-y opacity-40" style={{ animationDelay: '3s' }} />
      <div className="blob blob-coral w-[280px] h-[280px] top-32 left-1/3 float-y opacity-35" style={{ animationDelay: '5s' }} />

      {/* Subtle logo watermark (barely visible) */}
      <motion.div
        aria-hidden="true"
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 0.05 }}
        transition={{ duration: 2.5, delay: 0.3 }}
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
      >
        <img
          src={LOGO_URL}
          alt=""
          className="w-[70vw] max-w-[900px] h-auto object-contain"
          style={{ mixBlendMode: "multiply" }}
        />
      </motion.div>

      {/* Large logo — inline at top of hero (scrolls with the page, no white background) */}
      <div className="relative w-full flex justify-center mb-4 md:mb-8">
        <img
          src={LOGO_URL}
          alt="Les Couleurs de Manou — Maquilleuse artistique"
          className="w-[90vw] max-w-[1100px] h-auto pointer-events-none select-none"
          style={{ opacity: 0.95, mixBlendMode: "multiply" }}
          loading="eager"
          data-testid="hero-logo-large"
        />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center min-h-[calc(100svh-14rem)]">
        {/* Copy */}
        <div className="lg:col-span-7 relative z-10">
          <p
            data-testid="hero-eyebrow"
            className="text-xs uppercase tracking-[0.32em] font-medium flex items-center gap-3"
            style={{ color: "var(--ink-soft)" }}
          >
            <span className="w-8 h-px" style={{ background: "var(--pistachio)" }} />
            Maquilleuse artistique · Loiret (45)
          </p>

          <h1
            data-testid="hero-title"
            className="font-serif font-light text-5xl md:text-6xl lg:text-7xl mt-4 leading-[1.02]"
            style={{ color: "var(--ink)" }}
          >
            <LineReveal delay={0.35}>Les Couleurs</LineReveal>
            <br />
            <span className="italic" style={{ color: "var(--brand)" }}>
              <LineReveal delay={0.55}>de Manou.</LineReveal>
            </span>
          </h1>

          <p
            data-testid="hero-tagline"
            className="font-serif italic text-xl md:text-2xl mt-6 max-w-xl leading-relaxed"
            style={{ color: "var(--ink-soft)" }}
          >
            <WordsReveal
              text="Des couleurs, de la créativité et des sourires pour illuminer tous vos événements."
              baseDelay={0.85}
            />
          </p>

          <motion.p
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-8 text-lg max-w-xl leading-relaxed font-light"
            style={{ color: "var(--ink-soft)" }}
          >
            Bienvenue chez <em className="not-italic font-medium" style={{ color: "var(--ink)" }}>Les Couleurs de Manou</em>. Je mets ma créativité et ma passion au service de vos événements afin d&apos;offrir aux petits comme aux grands une parenthèse de magie et de couleur.
          </motion.p>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.55 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="btn-primary group"
            >
              Demander un devis
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <a
              href="#prestations"
              data-testid="hero-cta-secondary"
              className="btn-ghost group"
            >
              Découvrir mes prestations
              <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
            </a>
          </motion.div>
        </div>

        {/* Circular photo */}
        <motion.div
          className="lg:col-span-5 relative flex justify-center lg:justify-end"
          initial={reduce ? false : { opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          style={reduce ? {} : { y: yPhoto }}
        >
          {/* Watercolor aureole behind photo */}
          <Aureole className="absolute -top-8 -left-8" size={340} />
          <Aureole className="absolute -bottom-6 -right-6" size={220} />

          <motion.div
            style={reduce ? {} : { scale: scalePhoto }}
            className="relative w-[80%] md:w-[560px] aspect-square"
          >
            <div
              className="relative w-full h-full rounded-full overflow-hidden"
              style={{ boxShadow: "0 40px 80px -30px rgba(61,46,54,0.35), inset 0 0 0 8px rgba(255,255,255,0.9)" }}
            >
              <img
                src={photos.hero}
                alt="Enfant avec un maquillage artistique délicat, plein de joie."
                className="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
              />
            </div>
            {/* Handpainted heart badge */}
            <div className="absolute -bottom-4 -left-4 md:-left-6 rounded-full px-5 py-3 flex items-center gap-2 shadow-lg" style={{ background: "#FFFFFF" }}>
              <Heart className="w-4 h-4" style={{ color: "var(--brand)" }} fill="currentColor" />
              <span className="font-hand text-xl" style={{ color: "var(--ink)" }}>Hypoallergénique</span>
            </div>
            {/* Rotating brush accent */}
            <BrushIcon className="absolute -top-4 -right-4 w-16 h-16 float-y" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

/* ============================================================
   BRAND VALUES marquee — soft, editorial
   ============================================================ */
const MARQUEE_ITEMS = ["Douceur", "Créativité", "Bienveillance", "Poésie", "Élégance", "Magie", "Joie", "Confiance"];
const Marquee = () => (
  <section data-testid="marquee-section" aria-hidden="true" className="relative py-16 md:py-20 overflow-hidden" style={{ backgroundColor: "var(--bg-linen)" }}>
    <BrushDivider color="#F5A5B8" height={50} variant="ribbon" className="absolute top-0 inset-x-0 opacity-70" />
    <div className="marquee-track mt-4">
      {[0, 1].map((k) => (
        <div key={k} className="flex items-center gap-12 pr-12 shrink-0">
          {MARQUEE_ITEMS.map((w, i) => (
            <React.Fragment key={`${k}-${i}`}>
              <span className="font-serif italic text-5xl md:text-6xl font-light whitespace-nowrap" style={{ color: "var(--ink)" }}>
                {w}
              </span>
              <BrushIcon index={i} className="w-8 h-8 shrink-0" />
            </React.Fragment>
          ))}
        </div>
      ))}
    </div>
    <BrushDivider color="#C4B0E8" height={50} variant="swash" className="absolute bottom-0 inset-x-0 opacity-70" />
  </section>
);

/* ============================================================
   SERVICES — Prestations
   ============================================================ */
const EVENT_TAGS = [
  { label: "Anniversaires", bg: "#F5A5B8", text: "#8A2D45" },
  { label: "Mariages", bg: "#C4B0E8", text: "#4A2E7A" },
  { label: "Festivals", bg: "#FFB894", text: "#8B3D14" },
  { label: "Écoles", bg: "#B8D480", text: "#3A5E1A" },
  { label: "Centres de loisirs", bg: "#7EC8CE", text: "#1F5C64" },
  { label: "Marchés de Noël", bg: "#FF8D7B", text: "#7A2410" },
  { label: "Carnavals", bg: "#FFDD87", text: "#7A5A10" },
  { label: "Comités des fêtes", bg: "#F5A5B8", text: "#8A2D45" },
  { label: "Entreprises", bg: "#B8D480", text: "#3A5E1A" },
];

const Services = () => (
  <section id="prestations" data-testid="services-section" className="relative py-28 md:py-40 overflow-hidden" style={{ backgroundColor: "var(--bg)" }}>
    <div className="blob blob-blush w-[380px] h-[380px] top-40 -right-20" />
    <div className="blob blob-turquoise w-[300px] h-[300px] top-80 -left-16 opacity-40" />

    <div className="relative max-w-[1200px] mx-auto px-6 md:px-10">
      <div className="max-w-3xl mb-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] mb-4 flex items-center gap-3" style={{ color: "var(--pistachio)" }}>
            <span className="w-6 h-px" style={{ background: "var(--pistachio)" }} />
            01 — Mes prestations
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]" style={{ color: "var(--ink)" }}>
            Des prestations pour <em style={{ color: "var(--brand)" }}>tous vos événements</em>.
          </h2>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
        {/* Enfants */}
        <Reveal>
          <article data-testid="service-enfants" className="group relative rounded-[3rem] overflow-hidden aspect-[4/5] md:aspect-[3/4]" style={{ backgroundColor: "var(--ink)" }}>
            <img src={photos.serviceEnfants} alt="Maquillage artistique enfant" className="absolute inset-0 w-full h-full object-cover opacity-95 group-hover:scale-105 group-hover:opacity-100 transition-all duration-[1200ms]" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(75,59,66,0.8) 0%, rgba(75,59,66,0.2) 45%, transparent 70%)" }} />
            <div className="relative h-full flex flex-col justify-end p-8 md:p-10 text-white">
              <h3 className="font-serif text-3xl md:text-4xl mb-4">Maquillage artistique enfants</h3>
              <p className="text-white/90 leading-relaxed max-w-md font-light">
                Anniversaires, kermesses, écoles, centres de loisirs, arbres de Noël, carnavals — un maquillage sur-mesure pour chaque enfant.
              </p>
            </div>
          </article>
        </Reveal>
        {/* Adultes */}
        <Reveal delay={0.1}>
          <article data-testid="service-adultes" className="group relative rounded-[2.5rem] overflow-hidden aspect-[4/5] md:aspect-[3/4]" style={{ backgroundColor: "var(--ink)" }}>
            <img src={photos.serviceAdultes} alt="Maquillage artistique adulte" className="absolute inset-0 w-full h-full object-cover opacity-95 group-hover:scale-105 group-hover:opacity-100 transition-all duration-[1200ms]" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(61,46,54,0.85) 0%, rgba(61,46,54,0.25) 45%, transparent 70%)" }} />
            <div className="relative h-full flex flex-col justify-end p-8 md:p-10 text-white">
              <h3 className="font-serif text-3xl md:text-4xl mb-4">Maquillage artistique adultes</h3>
              <p className="text-white/90 leading-relaxed max-w-md font-light">
                Événements, soirées, spectacles et animations — un maquillage qui sublime votre présence sur scène ou en public.
              </p>
            </div>
          </article>
        </Reveal>
      </div>

      {/* Event tags */}
      <Reveal>
        <div className="mt-20 pt-10" style={{ borderTop: "1px solid var(--line)" }}>
          <p className="text-xs uppercase tracking-[0.3em] mb-6" style={{ color: "var(--pistachio)" }}>Interventions</p>
          <ul className="flex flex-wrap gap-x-3 gap-y-5">
            {EVENT_TAGS.map((tag, i) => (
              <li
                key={tag.label}
                data-testid={`event-tag-${i}`}
                className="relative inline-flex items-center justify-center px-6 py-3 text-sm font-bold transition-transform hover:-translate-y-0.5"
                style={{
                  color: tag.text,
                  transform: `rotate(${(i % 2 === 0 ? -1 : 1) * (0.5 + (i % 3) * 0.6)}deg)`,
                }}
              >
                <svg
                  viewBox="0 0 260 70"
                  preserveAspectRatio="none"
                  className="absolute inset-0 w-full h-full"
                  aria-hidden="true"
                >
                  <defs>
                    <filter id={`brush-blur-${i}`} x="-10%" y="-10%" width="120%" height="120%">
                      <feGaussianBlur stdDeviation="0.8" />
                    </filter>
                  </defs>
                  {/* Organic brush stroke shape */}
                  <path
                    d={
                      i % 3 === 0
                        ? "M6 26 C40 8, 90 6, 130 12 S 210 4, 254 22 C 258 34, 250 52, 226 58 S 130 66, 90 60 S 20 64, 8 48 S 2 34, 6 26 Z"
                        : i % 3 === 1
                        ? "M8 22 C30 4, 100 10, 138 16 S 220 8, 252 28 C 256 44, 240 56, 210 60 S 100 62, 60 56 S 12 60, 6 44 S 4 30, 8 22 Z"
                        : "M10 30 C50 10, 110 12, 150 18 S 220 6, 250 26 C 254 40, 244 54, 216 58 S 110 60, 70 54 S 14 58, 8 42 S 6 34, 10 30 Z"
                    }
                    fill={tag.bg}
                    opacity="0.95"
                    filter={`url(#brush-blur-${i})`}
                  />
                  {/* Paint drip accent */}
                  <circle cx={i * 20 + 30} cy="64" r="2" fill={tag.bg} opacity="0.7" />
                  <circle cx={i * 20 + 200} cy="66" r="1.4" fill={tag.bg} opacity="0.6" />
                </svg>
                <span className="relative z-10">{tag.label}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 flex items-center gap-2 text-lg font-serif italic" style={{ color: "#5A7A3F" }}>
            <MapPin className="w-4 h-4" /> Déplacements dans tout le Loiret.
          </p>
        </div>
      </Reveal>
    </div>
    <BrushDivider color="#C4B0E8" className="mt-24 -mb-4" variant="ribbon" />
  </section>
);

/* ============================================================
   TARIFS — Grille tarifaire
   ============================================================ */
const TARIF_CARDS = [
  {
    id: "anniversaires",
    icon: "🎂",
    title: "Anniversaires",
    color: "#F5A5B8",
    textColor: "#8A2D45",
    columns: ["Durée", "Enfants", "Tarif"],
    rows: [
      ["1 heure", "Jusqu'à 10 enfants", "65 €"],
      ["1 h 30", "Jusqu'à 15 enfants", "100 €"],
      ["2 heures", "Jusqu'à 20 enfants", "130 €"],
      ["Heure suppl.", "—", "40 €"],
    ],
    note: "Chaque enfant bénéficie d'un maquillage adapté à son âge.",
  },
  {
    id: "spectacles",
    icon: "✨",
    title: "Spectacles & Carnaval",
    color: "#C4B0E8",
    textColor: "#4A2E7A",
    columns: ["Prestation", "Durée", "Tarif"],
    rows: [
      ["Maquillage (continu)", "1 heure", "70 €"],
      ["Maquillage (continu)", "2 heures", "140 €"],
      ["Journée (continu)", "6 heures", "400 €"],
    ],
    note: "Idéal pour festivals, défilés, fêtes de ville, kermesses…",
  },
  {
    id: "evenements",
    icon: "🎪",
    title: "Événements",
    color: "#7EC8CE",
    textColor: "#1F5C64",
    columns: ["Prestation", "Durée", "Tarif"],
    rows: [
      ["Petit événement (arbres, kermesses…)", "2 heures", "170 €"],
      ["Événement moyen (fêtes, inaugurations…)", "4 heures", "320 €"],
      ["Grand événement (festivals, marchés…)", "6 heures", "450 €"],
      ["Heure suppl.", "—", "70 €"],
    ],
    note: "Devis personnalisé selon la nature et la durée de votre événement.",
  },
];

const OPTIONS_ROWS = [
  { label: "Frais de déplacement", detail: "au-delà de 20 km autour d'Orléans", value: "0,60 € / km" },
  { label: "Forfait soirée / nocturne", detail: "à partir de 19 h", value: "+ 20 %" },
  { label: "Supports pailletés / strass", detail: "sur le maquillage", value: "Inclus" },
  { label: "Maquillage spécifique", detail: "demande particulière", value: "Sur devis" },
];

const TarifCard = ({ card, i }) => (
  <Reveal delay={i * 0.08}>
    <article
      data-testid={`tarif-${card.id}`}
      className="relative h-full rounded-[2.5rem] p-8 md:p-10 overflow-hidden"
      style={{ background: "#fff", boxShadow: "0 30px 60px -30px rgba(75,59,66,0.18)" }}
    >
      <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: card.color }} />
      <div className="flex items-center gap-3 mb-6">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center text-xl"
          style={{ background: card.color }}
        >
          <span aria-hidden="true">{card.icon}</span>
        </div>
        <h3 className="font-serif text-2xl md:text-[1.6rem]" style={{ color: card.textColor }}>{card.title}</h3>
      </div>

      <div className="grid" style={{ gridTemplateColumns: card.columns.length === 3 ? "1.5fr 1.5fr auto" : "auto" }}>
        {card.columns.map((c, ci) => (
          <div
            key={ci}
            className="text-[10px] uppercase tracking-[0.2em] font-semibold pb-3 border-b"
            style={{ color: card.textColor, borderColor: "var(--line)" }}
          >
            {c}
          </div>
        ))}
        {card.rows.map((row, ri) => (
          row.map((cell, ci) => (
            <div
              key={`${ri}-${ci}`}
              className={`py-3 border-b last:border-b-0 ${ci === row.length - 1 ? "text-right" : ""}`}
              style={{
                color: ci === row.length - 1 ? card.textColor : "var(--ink)",
                borderColor: "var(--line)",
                fontWeight: ci === row.length - 1 ? 700 : 400,
                fontSize: ci === row.length - 1 ? "1.15rem" : "0.95rem",
              }}
            >
              {cell}
            </div>
          ))
        ))}
      </div>

      <p className="mt-6 flex items-start gap-2 text-sm font-serif italic" style={{ color: "var(--ink-soft)" }}>
        <Heart className="w-4 h-4 mt-0.5 shrink-0" style={{ color: card.color }} fill="currentColor" />
        {card.note}
      </p>
    </article>
  </Reveal>
);

const Tarifs = () => (
  <section id="tarifs" data-testid="tarifs-section" className="relative py-28 md:py-40 overflow-hidden" style={{ backgroundColor: "var(--bg)" }}>
    <BrushDivider color="#FFB894" height={60} variant="ribbon" className="absolute top-0 inset-x-0 opacity-60" />
    <div className="blob blob-blush w-[380px] h-[380px] top-40 -left-20" />
    <div className="blob blob-lavender w-[320px] h-[320px] bottom-40 right-0 opacity-40" />
    <div className="blob blob-buttercream w-[280px] h-[280px] top-1/2 right-1/4 opacity-40" />

    <div className="relative max-w-[1200px] mx-auto px-6 md:px-10">
      <div className="max-w-3xl mb-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] mb-4 flex items-center gap-3" style={{ color: "var(--pistachio)" }}>
            <span className="w-6 h-px" style={{ background: "var(--pistachio)" }} />
            Grille tarifaire
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl" style={{ color: "var(--ink)" }}>
            Des tarifs <em style={{ color: "var(--brand)" }}>clairs</em> pour tous vos événements.
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 text-lg font-light max-w-2xl" style={{ color: "var(--ink-soft)" }}>
            Devis personnalisé sur demande selon la nature et la durée de votre événement.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        {TARIF_CARDS.map((c, i) => <TarifCard key={c.id} card={c} i={i} />)}

        <Reveal delay={0.24}>
          <article
            data-testid="tarif-options"
            className="relative h-full rounded-[2.5rem] p-8 md:p-10 overflow-hidden"
            style={{ background: "#fff", boxShadow: "0 30px 60px -30px rgba(75,59,66,0.18)" }}
          >
            <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: "#FF8D7B" }} />
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl" style={{ background: "#FF8D7B" }}>
                <span aria-hidden="true">🎨</span>
              </div>
              <h3 className="font-serif text-2xl md:text-[1.6rem]" style={{ color: "#7A2410" }}>Options &amp; Frais</h3>
            </div>
            <div className="space-y-1">
              {OPTIONS_ROWS.map((row, i) => (
                <div key={i} className="flex items-baseline justify-between gap-4 py-3 border-b last:border-b-0" style={{ borderColor: "var(--line)" }}>
                  <div>
                    <p className="font-semibold" style={{ color: "var(--ink)" }}>{row.label}</p>
                    <p className="text-sm" style={{ color: "var(--ink-soft)" }}>{row.detail}</p>
                  </div>
                  <span className="font-bold text-lg whitespace-nowrap" style={{ color: "#8B3D14" }}>{row.value}</span>
                </div>
              ))}
            </div>
          </article>
        </Reveal>
      </div>

      <Reveal delay={0.3}>
        <div className="mt-16 rounded-[2rem] p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" style={{ background: "var(--bg-linen)" }}>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "#D48699" }}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-serif text-xl md:text-2xl" style={{ color: "var(--ink)" }}>Une prestation sur-mesure ?</p>
              <p className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}>
                Je vous accompagne pour construire un devis adapté à votre événement.
              </p>
            </div>
          </div>
          <a href="#contact" data-testid="tarifs-cta" className="btn-primary shrink-0">
            Demander un devis
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ============================================================
   VALUES — Pourquoi choisir
   ============================================================ */
const VALUES = [
  { icon: Sparkles, title: "Produits professionnels hypoallergéniques", desc: "Des marques reconnues du face painting, testées et sûres pour tous.", color: "#F5A5B8" },
  { icon: Palette, title: "Créations personnalisées", desc: "Chaque visage devient une œuvre unique, imaginée avec l'enfant ou l'adulte.", color: "#C4B0E8" },
  { icon: Heart, title: "Maquillages détaillés", desc: "Un vrai soin du trait, des couleurs et des finitions pailletées.", color: "#FF8D7B" },
  { icon: Clock, title: "Rapidité d'exécution", desc: "Un rythme fluide qui garde l'énergie de la fête, sans faire attendre.", color: "#B8D4A8" },
  { icon: Baby, title: "Bienveillance avec les enfants", desc: "De la douceur, du jeu et de l'écoute — les timides adorent aussi.", color: "#FFDD87" },
  { icon: MapPin, title: "Déplacements dans tout le Loiret", desc: "Autour d'Orléans et bien au-delà — je viens là où vous êtes.", color: "#7EC8CE" },
];

const Values = () => (
  <section id="valeurs" data-testid="values-section" className="relative py-28 md:py-40 overflow-hidden" style={{ backgroundColor: "var(--bg-linen)" }}>
    <BrushDivider color="#7EC8CE" height={60} variant="ribbon" className="absolute top-0 inset-x-0 opacity-60" />
    <div className="blob blob-peach w-[420px] h-[420px] -top-24 -left-32" />
    <div className="blob blob-turquoise w-[420px] h-[420px] bottom-0 -right-24" />
    <div className="blob blob-buttercream w-[300px] h-[300px] top-1/2 left-1/3 opacity-40" />

    <div className="relative max-w-[1200px] mx-auto px-6 md:px-10">
      <div className="max-w-3xl mb-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] mb-4 flex items-center gap-3" style={{ color: "var(--pistachio)" }}>
            <span className="w-6 h-px" style={{ background: "var(--pistachio)" }} />
            02 — Ce qui rend Manou unique
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl" style={{ color: "var(--ink)" }}>
            Pourquoi choisir <em style={{ color: "var(--brand)" }}>Les Couleurs de Manou</em> ?
          </h2>
        </Reveal>
      </div>
    </div>

    {/* Horizontal auto-scrolling row of value cards (right → left) */}
    <div
      data-testid="values-marquee"
      className="relative overflow-hidden"
      style={{
        maskImage: "linear-gradient(to right, transparent 0, #000 6%, #000 94%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(to right, transparent 0, #000 6%, #000 94%, transparent 100%)",
      }}
    >
      <div className="marquee-track py-4" style={{ animationDuration: "48s" }}>
        {[0, 1].map((k) => (
          <div key={k} className="flex items-stretch gap-6 pr-6 shrink-0">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={`${k}-${i}`}
                  data-testid={k === 0 ? `value-${i}` : undefined}
                  className="relative w-[320px] md:w-[360px] shrink-0 rounded-[2.5rem] p-8 md:p-10 transition-transform hover:-translate-y-1"
                  style={{ background: "#fff", boxShadow: "0 20px 40px -25px rgba(75,59,66,0.15)" }}
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-6"
                    style={{ background: v.color }}
                  >
                    <Icon className="w-6 h-6" style={{ color: "var(--ink)" }} strokeWidth={1.6} />
                  </div>
                  <h3 className="font-serif text-2xl md:text-[1.6rem] leading-tight mb-3" style={{ color: "var(--ink)" }}>{v.title}</h3>
                  <p className="leading-relaxed font-light" style={{ color: "var(--ink-soft)" }}>{v.desc}</p>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================
   MANIFESTO — À propos (histoire de passion)
   ============================================================ */
const CHAPTERS = [
  {
    n: "01",
    title: "Une passion pour la couleur",
    body:
      "Depuis toujours attirée par les couleurs, la peinture et le dessin, j'ai fait de cette sensibilité artistique le cœur de mon métier. Chaque prestation est une nouvelle occasion de créer, d'imaginer et de faire rêver.",
  },
  {
    n: "02",
    title: "Un savoir-faire dédié aux enfants",
    body:
      "Formée au contact des enfants, j'attache une attention particulière à leur confort et à leur émerveillement. Les timides comme les plus vifs trouvent toujours le maquillage qui leur ressemble.",
  },
  {
    n: "03",
    title: "Des créations sur-mesure",
    body:
      "Papillons, tigres, princesses, super-héros, motifs originaux… chaque visage devient une œuvre unique, réalisée à main levée, avec un vrai soin du trait et des finitions pailletées.",
  },
  {
    n: "04",
    title: "Une exigence de qualité",
    body:
      "J'utilise exclusivement des produits professionnels hypoallergéniques, adaptés aux peaux sensibles et faciles à démaquiller — pour une prestation soignée, sûre et pleinement respectueuse de chacun.",
  },
];

const Manifesto = () => (
  <section id="histoire" data-testid="manifesto-section" className="relative py-28 md:py-40 overflow-hidden" style={{ backgroundColor: "var(--bg)" }}>
    <BrushDivider color="#B8D4A8" height={60} variant="ribbon" className="absolute top-0 inset-x-0 opacity-60" />
    <div className="blob blob-lavender w-[420px] h-[420px] top-40 -left-32" />
    <div className="blob blob-peach w-[380px] h-[380px] bottom-20 -right-24" />
    <div className="blob blob-turquoise w-[280px] h-[280px] top-1/3 right-1/4 opacity-30" />

    <div className="relative max-w-[1200px] mx-auto px-6 md:px-10">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-20">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] mb-4 flex items-center gap-3" style={{ color: "var(--pistachio)" }}>
              <span className="w-6 h-px" style={{ background: "var(--pistachio)" }} /> 03 — À propos
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02]" style={{ color: "var(--ink)" }}>
              Derrière <em style={{ color: "var(--brand)" }}>Les&nbsp;Couleurs de Manou</em>.
            </h2>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-8 font-hand text-3xl md:text-4xl max-w-sm" style={{ color: "var(--ink)" }}>
              Un univers artistique tout en couleurs, en douceur et en émotion.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.2}>
            <div className="relative">
              <Aureole className="absolute -top-4 -left-4" size={200} />
              <div
                className="relative aspect-[3/4] rounded-[2.5rem] overflow-hidden"
                style={{ boxShadow: "0 30px 60px -30px rgba(61,46,54,0.35), inset 0 0 0 6px rgba(255,255,255,0.9)" }}
              >
                <img src={photos.portrait} alt="Portrait de Manou en train de créer." className="w-full h-full object-cover" />
              </div>
              <BrushIcon className="absolute -bottom-6 -right-6 w-20 h-20 float-y" />
            </div>
          </Reveal>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.08}>
            <article data-testid={`chapter-${c.n}`} className="relative pt-4">
              <BrushDivider color="#F5A5B8" height={40} variant="swash" className="-mt-2 mb-6" />
              <div className="flex items-baseline gap-4 mb-3">
                <span className="font-serif text-4xl md:text-5xl" style={{ color: "var(--ink-mute)" }}>{c.n}</span>
                <h3 className="font-serif text-2xl md:text-[1.75rem]" style={{ color: "var(--ink)" }}>{c.title}</h3>
              </div>
              <p className="leading-relaxed font-light text-lg" style={{ color: "var(--ink-soft)" }}>{c.body}</p>
            </article>
          </Reveal>
        ))}
      </div>

      {/* Citation */}
      <Reveal>
        <blockquote data-testid="quote-citation" className="mt-28 md:mt-36 max-w-4xl mx-auto text-center relative">
          <div className="blob blob-blush w-[300px] h-[300px] -top-24 left-1/2 -translate-x-1/2 opacity-70" />
          <svg viewBox="0 0 60 40" className="w-10 h-10 mx-auto mb-6" fill="var(--brand)" aria-hidden="true">
            <path d="M14 32c-6 0-10-4-10-10 0-8 6-16 16-20l2 6c-6 2-10 6-12 12h4c4 0 6 2 6 6s-2 6-6 6zm26 0c-6 0-10-4-10-10 0-8 6-16 16-20l2 6c-6 2-10 6-12 12h4c4 0 6 2 6 6s-2 6-6 6z" />
          </svg>
          <p className="font-serif italic text-2xl md:text-3xl lg:text-4xl leading-[1.3]" style={{ color: "var(--ink)" }}>
            Pour moi, chaque visage est une toile, chaque enfant une source d&apos;inspiration et chaque événement une nouvelle occasion de créer des souvenirs inoubliables.
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.32em]" style={{ color: "var(--pistachio)" }}>— Manou</p>
        </blockquote>
      </Reveal>
    </div>
  </section>
);

/* ============================================================
   GALLERY — Instants de magie
   ============================================================ */
const CATEGORIES = [
  { key: "enfants", label: "Enfants" },
  { key: "adultes", label: "Adultes" },
  { key: "evenements", label: "Événements" },
];

const Gallery = () => {
  const [cat, setCat] = useState("enfants");
  const items = photos.gallery[cat] || [];
  return (
    <section id="galerie" data-testid="gallery-section" className="relative py-28 md:py-40 overflow-hidden" style={{ backgroundColor: "var(--bg-linen)" }}>
      <BrushDivider color="#FFB894" height={60} variant="wave" className="absolute top-0 inset-x-0 opacity-60" />
      <div className="blob blob-buttercream w-[380px] h-[380px] top-40 right-0" />
      <div className="blob blob-coral w-[320px] h-[320px] bottom-0 left-1/4 opacity-40" />

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] mb-4 flex items-center gap-3" style={{ color: "var(--pistachio)" }}>
                <span className="w-6 h-px" style={{ background: "var(--pistachio)" }} /> 04 — Galerie
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl" style={{ color: "var(--ink)" }}>
                Instants de <em style={{ color: "var(--brand)" }}>magie</em>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div role="tablist" data-testid="gallery-tabs" className="inline-flex items-center gap-1 p-1 rounded-full" style={{ background: "#fff", border: "1px solid var(--line)" }}>
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  role="tab"
                  aria-selected={cat === c.key}
                  data-testid={`gallery-tab-${c.key}`}
                  onClick={() => setCat(c.key)}
                  className={`relative px-5 py-2 text-sm font-medium rounded-full transition-colors ${cat === c.key ? "text-white" : ""}`}
                  style={{ color: cat === c.key ? "#fff" : "var(--ink-soft)" }}
                >
                  {cat === c.key && (
                    <motion.span
                      layoutId="gallery-pill"
                      className="absolute inset-0 rounded-full"
                      style={{ background: "#D48699" }}
                      transition={{ type: "spring", stiffness: 320, damping: 30 }}
                    />
                  )}
                  <span className="relative">{c.label}</span>
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <motion.div
          key={cat}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5"
        >
          {items.map((p, i) => {
            const spans = ["row-span-2 aspect-[3/4]", "aspect-square", "row-span-2 aspect-[3/4]", "aspect-square", "aspect-square", "row-span-2 aspect-[3/4]"];
            const radii = ["rounded-[2rem]", "rounded-[2.5rem]", "rounded-[2rem]", "rounded-[2.5rem]", "rounded-[2rem]", "rounded-[2.5rem]"];
            return (
              <motion.figure
                key={`${cat}-${i}`}
                data-testid={`gallery-item-${cat}-${i}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className={`relative overflow-hidden ${spans[i % spans.length]} ${radii[i % radii.length]}`}
                style={{ background: "#fff" }}
              >
                <img src={p.src} alt={p.alt} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" />
              </motion.figure>
            );
          })}
        </motion.div>

        <p className="mt-10 text-sm font-hand text-xl" style={{ color: "var(--ink-mute)" }}>
          Ces images seront prochainement remplacées par les créations originales de Manou.
        </p>
      </div>
    </section>
  );
};

/* ============================================================
   FAQ
   ============================================================ */
const FAQS = [
  { q: "Vous déplacez-vous dans tout le Loiret ?", a: "Oui, je me déplace dans l'ensemble du département — d'Orléans à Montargis, Gien, Pithiviers et alentours." },
  { q: "Les produits sont-ils adaptés aux enfants ?", a: "Oui, j'utilise exclusivement des produits professionnels hypoallergéniques, testés pour les peaux sensibles et faciles à démaquiller à l'eau et au savon." },
  { q: "Combien de temps dure un maquillage ?", a: "Cela dépend du modèle choisi : entre quelques minutes pour un motif simple et une quinzaine de minutes pour un maquillage très détaillé." },
  { q: "Comment demander un devis ?", a: "Il suffit de remplir le formulaire de contact sur le site — je vous réponds dans les meilleurs délais avec une proposition adaptée à votre projet." },
  { q: "Intervenez-vous pour les écoles ?", a: "Oui, avec plaisir : kermesses, arbres de Noël, événements pédagogiques, journées à thème dans les écoles et centres de loisirs." },
];

const FAQItem = ({ item, index, open, onToggle }) => (
  <div data-testid={`faq-item-${index}`} style={{ borderTop: "1px solid var(--line)" }}>
    <button
      onClick={onToggle}
      data-testid={`faq-toggle-${index}`}
      className="w-full flex items-start justify-between gap-6 py-8 text-left"
      aria-expanded={open}
    >
      <span className="font-serif text-xl md:text-2xl lg:text-3xl" style={{ color: "var(--ink)" }}>
        {item.q}
      </span>
      <span
        className={`mt-1 shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${open ? "rotate-180" : ""}`}
        style={{
          border: `1px solid ${open ? "#D48699" : "var(--line)"}`,
          background: open ? "#D48699" : "transparent",
          color: open ? "#fff" : "var(--ink)",
        }}
      >
        <ChevronDown className="w-4 h-4" />
      </span>
    </button>
    <motion.div
      initial={false}
      animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      style={{ overflow: "hidden" }}
    >
      <p className="pb-8 pr-14 text-lg leading-relaxed font-light max-w-3xl" style={{ color: "var(--ink-soft)" }}>{item.a}</p>
    </motion.div>
  </div>
);

const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section id="faq" data-testid="faq-section" className="relative py-28 md:py-40 overflow-hidden" style={{ backgroundColor: "var(--bg)" }}>
      <BrushDivider color="#C4B0E8" height={60} variant="swash" className="absolute top-0 inset-x-0 opacity-60" />
      <div className="blob blob-sage w-[380px] h-[380px] top-40 right-0" />
      <div className="blob blob-turquoise w-[300px] h-[300px] bottom-20 -left-20 opacity-40" />
      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] mb-4 flex items-center gap-3" style={{ color: "var(--pistachio)" }}>
              <span className="w-6 h-px" style={{ background: "var(--pistachio)" }} /> 05 — FAQ
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] sticky top-28" style={{ color: "var(--ink)" }}>
              Questions <em style={{ color: "var(--brand)" }}>fréquentes</em>.
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          <div style={{ borderBottom: "1px solid var(--line)" }}>
            {FAQS.map((f, i) => (
              <FAQItem
                key={i}
                index={i}
                item={f}
                open={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   CONTACT
   ============================================================ */
const Contact = () => (
  <section id="contact" data-testid="contact-section" className="relative py-28 md:py-40 overflow-hidden" style={{ backgroundColor: "var(--bg-linen)" }}>
    <BrushDivider color="#FF8D7B" height={60} variant="ribbon" className="absolute top-0 inset-x-0 opacity-60" />
    <div className="blob blob-lavender w-[420px] h-[420px] -top-24 -right-40" />
    <div className="blob blob-blush w-[360px] h-[360px] bottom-10 -left-24" />
    <div className="blob blob-coral w-[280px] h-[280px] top-1/2 right-1/3 opacity-40" />

    <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] mb-4 flex items-center gap-3" style={{ color: "var(--pistachio)" }}>
            <span className="w-6 h-px" style={{ background: "var(--pistachio)" }} /> 06 — Contact
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02]" style={{ color: "var(--ink)" }}>
            Parlons de votre <em style={{ color: "var(--brand)" }}>événement</em>.
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 text-lg max-w-md leading-relaxed font-light" style={{ color: "var(--ink-soft)" }}>
            Vous souhaitez apporter une touche de magie à votre événement ? Je serai ravie d&apos;échanger avec vous afin de construire une prestation adaptée à vos envies.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 space-y-4">
            <a href="mailto:lescouleursdemanou@gmail.com" data-testid="contact-email" className="group flex items-center gap-3 font-medium" style={{ color: "var(--ink)" }}>
              <Mail className="w-5 h-5" />
              <span className="pb-0.5" style={{ borderBottom: "1px solid var(--line)" }}>lescouleursdemanou@gmail.com</span>
            </a>
            <p className="flex items-center gap-3" style={{ color: "var(--ink-soft)" }}>
              <MapPin className="w-5 h-5" /> Zone d&apos;intervention : tout le Loiret (45).
            </p>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <Reveal delay={0.15}>
          <QuoteForm />
        </Reveal>
      </div>
    </div>
  </section>
);

/* ============================================================
   FOOTER
   ============================================================ */
const SocialLink = ({ Icon, label, href, testId }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="group relative inline-flex items-center gap-2 rounded-full px-4 py-2 transition-all hover:-translate-y-0.5"
    style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}
  >
    <Icon className="w-4 h-4" />
    <span className="text-sm font-medium">{label}</span>
    <ArrowUpRight className="w-3 h-3 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
  </a>
);

const SocialSoon = ({ Icon, label, slug }) => (
  <span
    data-testid={`social-${slug}`}
    aria-disabled="true"
    className="group relative inline-flex items-center gap-2 rounded-full px-4 py-2 cursor-not-allowed"
    style={{ border: "1px solid rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.6)" }}
    title="À venir"
  >
    <Icon className="w-4 h-4" />
    <span className="text-sm">{label}</span>
    <span className="ml-1 text-[10px] uppercase tracking-widest">à venir</span>
  </span>
);

/* Snapchat + TikTok icons (not in lucide) */
const SnapchatIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M12 2.5c3.3 0 5.5 2.6 5.5 5.8 0 1 .1 1.9.5 2.3.4.4 1 .3 1.4.6.4.3.5.9-.2 1.2-.7.3-1.7.5-2.1 1.3-.4.7 1.1 2.5 3.4 3 .3.1.4.4.3.7-.5 1.3-3.2 1.4-3.7 2.1-.5.8.4 2.1-.7 2.4-1.1.3-2.7-.6-4.2-.4-1.4.2-2.4 1.8-4.2 1.8-1.7 0-2.7-1.6-4.2-1.8-1.5-.2-3.1.7-4.2.4-1.1-.3-.2-1.6-.7-2.4-.5-.7-3.2-.8-3.7-2.1-.1-.3 0-.6.3-.7 2.3-.5 3.8-2.3 3.4-3-.4-.8-1.4-1-2.1-1.3-.7-.3-.6-.9-.2-1.2.4-.3 1-.2 1.4-.6.4-.4.5-1.3.5-2.3C6.5 5.1 8.7 2.5 12 2.5z"/>
  </svg>
);
const TiktokIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.6 6.3c-1.7-.4-3-1.7-3.4-3.3 0-.3 0-.6-.1-.9v-.6h-3.5v13.7c0 1.6-1.3 2.9-2.9 2.9-1.6 0-2.9-1.3-2.9-2.9 0-1.6 1.3-2.9 2.9-2.9.3 0 .6 0 .8.1v-3.6c-.3 0-.6-.1-.8-.1-3.5 0-6.4 2.9-6.4 6.4S6.2 22 9.7 22s6.4-2.9 6.4-6.4v-6.9c1.4 1 3.1 1.6 4.9 1.6V6.9c-.6 0-1-.2-1.4-.6z"/>
  </svg>
);

const Footer = () => (
  <footer data-testid="site-footer" className="relative pt-24 pb-10 overflow-hidden" style={{ backgroundColor: "#5A7A3F", color: "rgba(255,255,255,0.85)" }}>
    <BrushDivider color="#B8D480" height={60} variant="swash" className="absolute top-0 inset-x-0 opacity-50" />

    <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-12">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-6">
          <LogoMark wrapperClass="h-24 md:h-28 -ml-2" />
          <p className="mt-6 max-w-md leading-relaxed font-light" style={{ color: "rgba(255,255,255,0.75)" }}>
            Des couleurs, de la créativité et des sourires pour illuminer tous vos événements.
          </p>
          <a
            href="https://lescouleursdemanouloiret.fr"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-website"
            className="mt-6 inline-flex items-center gap-2 font-serif italic text-lg hover:opacity-90"
            style={{ color: "#FFDD87" }}
          >
            lescouleursdemanouloiret.fr
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
        <div className="lg:col-span-3 lg:col-start-8">
          <p className="text-xs uppercase tracking-[0.3em] mb-6" style={{ color: "rgba(255,255,255,0.65)" }}>Navigation</p>
          <ul className="space-y-3">
            {NAV.map((l) => (
              <li key={l.href}>
                <a href={l.href} className="hover:text-white transition-colors">{l.label}</a>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-3">
          <p className="text-xs uppercase tracking-[0.3em] mb-6" style={{ color: "rgba(255,255,255,0.65)" }}>Retrouvez-moi</p>
          <a href="mailto:lescouleursdemanou@gmail.com" className="block hover:text-white mb-6 break-all">
            lescouleursdemanou@gmail.com
          </a>
          <div className="flex flex-wrap gap-2">
            <SocialLink Icon={SnapchatIcon} label="Snapchat" href="https://www.snapchat.com/add/couleursdemanou" testId="social-snapchat" />
            <SocialLink Icon={TiktokIcon} label="TikTok" href="https://www.tiktok.com/@lescouleursdemanou" testId="social-tiktok" />
            <SocialSoon Icon={Globe} label="Google Business" slug="google-business" />
            <SocialSoon Icon={Facebook} label="Facebook" slug="facebook" />
            <SocialSoon Icon={Instagram} label="Instagram" slug="instagram" />
          </div>
          <p className="mt-4 text-xs" style={{ color: "rgba(255,255,255,0.65)" }}>
            Snapchat : <span className="text-white">couleursdemanou</span><br />
            TikTok : <span className="text-white">@lescouleursdemanou</span>
          </p>
        </div>
      </div>

      <div className="mt-20 pt-8 flex flex-col md:flex-row justify-between gap-4 text-xs" style={{ borderTop: "1px solid rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.7)" }}>
        <p>© {new Date().getFullYear()} Les Couleurs de Manou — Tous droits réservés.</p>
        <p className="font-hand text-lg" style={{ color: "rgba(255,255,255,0.9)" }}>Maquilleuse artistique dans le Loiret ✿</p>
      </div>
    </div>
  </footer>
);

/* ============================================================
   PAGE
   ============================================================ */
export default function Home() {
  useLenis();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Les Couleurs de Manou",
    description:
      "Maquilleuse artistique dans le Loiret. Maquillage artistique pour enfants et adultes lors d'anniversaires, mariages, festivals, écoles, centres de loisirs, entreprises, carnavals et marchés de Noël.",
    email: "lescouleursdemanou@gmail.com",
    areaServed: { "@type": "AdministrativeArea", name: "Loiret" },
    address: { "@type": "PostalAddress", addressRegion: "Loiret", addressCountry: "FR" },
    url: "https://lescouleursdemanouloiret.fr",
    priceRange: "€€",
    image: photos.hero,
    logo: LOGO_URL,
  };

  return (
    <div data-testid="home-page" style={{ backgroundColor: "var(--bg)" }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Nav />
      <main>
        <Hero />
        <Marquee />
        <Services />
        <Tarifs />
        <Values />
        <Manifesto />
        <Gallery />
        <FAQ />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
